import type { MarkdownRenderResult } from '@/shared/markdown/renderMarkdown'

export interface MarkdownPreviewRequest {
  id: number
  markdown: string
}

export type MarkdownPreviewResponse =
  | { id: number; ok: true; result: MarkdownRenderResult }
  | { id: number; ok: false; error: 'size_limit' | 'processing_failed' }

export interface MarkdownWorkerLike {
  onmessage: ((event: { data: MarkdownPreviewResponse }) => void) | null
  onerror: (() => void) | null
  postMessage: (request: MarkdownPreviewRequest) => void
  terminate: () => void
}

export interface MarkdownPreviewCallbacks {
  success: (result: MarkdownRenderResult) => void
  error: (code: 'size_limit' | 'processing_failed') => void
}

export interface MarkdownPreviewController {
  render: (markdown: string) => void
  cancel: () => void
  dispose: () => void
}

export function createMarkdownPreviewController(
  createWorker: () => MarkdownWorkerLike,
  callbacks: MarkdownPreviewCallbacks,
): MarkdownPreviewController {
  let worker: MarkdownWorkerLike | null = null
  let requestId = 0
  let disposed = false

  function cancel(): void {
    requestId += 1
    worker?.terminate()
    worker = null
  }

  return {
    render(markdown) {
      if (disposed) return
      cancel()

      const id = requestId
      let currentWorker: MarkdownWorkerLike
      try {
        currentWorker = createWorker()
      } catch {
        callbacks.error('processing_failed')
        return
      }

      worker = currentWorker
      try {
        currentWorker.onmessage = (event) => {
          if (disposed || worker !== currentWorker || event.data.id !== id) return
          currentWorker.terminate()
          worker = null
          if (event.data.ok) callbacks.success(event.data.result)
          else callbacks.error(event.data.error)
        }
        currentWorker.onerror = () => {
          if (disposed || worker !== currentWorker) return
          currentWorker.terminate()
          worker = null
          callbacks.error('processing_failed')
        }
        currentWorker.postMessage({ id, markdown })
      } catch {
        currentWorker.terminate()
        if (worker === currentWorker) worker = null
        callbacks.error('processing_failed')
      }
    },
    cancel,
    dispose() {
      disposed = true
      cancel()
    },
  }
}

export function createBrowserMarkdownWorker(): MarkdownWorkerLike {
  return new Worker(
    new URL('./markdownPreview.worker.ts', import.meta.url),
    { type: 'module' },
  ) as unknown as MarkdownWorkerLike
}
